// ─── Global Layout ─────────────────────────────────────────────────────────────

import React, { ReactNode, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useQuery } from 'react-query';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../utils/helpers';
import ThemeToggle from '../ui/ThemeToggle';
import Spinner from '../ui/Spinner';
import { RouteErrorBoundary } from '../ui/ErrorBoundary';
import logoWhite from '../../assets/brand/ucarz-logo-white.png';
import { messagingService, MESSAGING_QUERY_KEY } from '../../api/messagingService';

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: ReactNode;
  badge?: string;
  matchPrefix?: string;
  altPrefix?: string;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M3.75 6A2.25 2.25 0 016 3.75h3A2.25 2.25 0 0111.25 6v3A2.25 2.25 0 019 11.25H6A2.25 2.25 0 013.75 9V6zm9 0A2.25 2.25 0 0115 3.75h3A2.25 2.25 0 0120.25 6v3A2.25 2.25 0 0118 11.25h-3A2.25 2.25 0 0112.75 9V6zM3.75 15A2.25 2.25 0 016 12.75h3A2.25 2.25 0 0111.25 15v3A2.25 2.25 0 019 20.25H6A2.25 2.25 0 013.75 18v-3zm9 0A2.25 2.25 0 0115 12.75h3A2.25 2.25 0 0120.25 15v3A2.25 2.25 0 0118 20.25h-3A2.25 2.25 0 0112.75 18v-3z" />
          </svg>
        ),
      },
      {
        id: 'dispatch',
        label: 'Dispatch Dashboard',
        path: '/dispatch',
        matchPrefix: '/dispatch',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.24 5.76L14.12 14.12 7.76 16.24 9.88 9.88l6.36-2.12z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'fleet',
    label: 'Fleet & People',
    items: [
      {
        id: 'drivers',
        label: 'Drivers',
        path: '/drivers',
        matchPrefix: '/drivers',
        altPrefix: '/onboarding',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      },
      {
        id: 'drivers-on-shift',
        label: 'Drivers on Shift',
        path: '/drivers/on-shift',
        matchPrefix: '/drivers/on-shift',
        altPrefix: '/shifts',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 5v5l4 2" />
          </svg>
        ),
      },
      {
        id: 'vehicles',
        label: 'Vehicles',
        path: '/vehicles',
        matchPrefix: '/vehicles',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M8 17h8M3 11l2-5h14l2 5M5 11v6h14v-6M7 17v1a1 1 0 002 0v-1m6 0v1a1 1 0 002 0v-1" />
          </svg>
        ),
      },
      {
        id: 'pas',
        label: 'Passenger Assistants',
        path: '/pas',
        matchPrefix: '/pas',
        altPrefix: '/pa-onboarding',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
      },
      {
        id: 'passengers',
        label: 'Passengers',
        path: '/passengers',
        matchPrefix: '/passengers',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'bookings',
        label: 'Bookings',
        path: '/bookings',
        matchPrefix: '/bookings',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
      },
      {
        id: 'places',
        label: 'Places',
        path: '/places',
        matchPrefix: '/places',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        ),
      },
      {
        id: 'messages',
        label: 'Messages',
        path: '/messages',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    items: [
      {
        id: 'driver-monthly-payment',
        label: 'Driver Payments',
        path: '/reports/drivers/monthly-payment',
        matchPrefix: '/reports/drivers/monthly-payment',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M9 7h6M9 11h6M9 15h4M5 5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5z" />
          </svg>
        ),
      },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    items: [
      {
        id: 'accounts',
        label: 'Accounts',
        path: '/accounts',
        matchPrefix: '/accounts',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        ),
      },
      {
        id: 'training',
        label: 'Training',
        path: '/training',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          </svg>
        ),
      },
      {
        id: 'option-sets',
        label: 'Option Sets',
        path: '/admin/option-sets',
        matchPrefix: '/admin/option-sets',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        ),
      },
      {
        id: 'working-hours-preferences',
        label: 'Working Hours',
        path: '/admin/working-hours-preferences',
        matchPrefix: '/admin/working-hours-preferences',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        id: 'bulk-import',
        label: 'Bulk Import',
        path: '/admin/bulk-import',
        matchPrefix: '/admin/bulk-import',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4m0 0l-4 4m4-4l4 4" />
          </svg>
        ),
      },
      {
        id: 'holidays',
        label: 'Public Holidays',
        path: '/holidays',
        matchPrefix: '/holidays',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        id: 'licensing-councils',
        label: 'Councils',
        path: '/licensing-councils',
        matchPrefix: '/licensing-councils',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M3 21h18M5 21V9l7-4 7 4v12M9 13h2m4 0h2M9 17h2m4 0h2" />
          </svg>
        ),
      },
      {
        id: 'subsidiaries',
        label: 'Subsidiaries',
        path: '/subsidiaries',
        matchPrefix: '/subsidiaries',
        icon: (
          <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2m-2 0V8a2 2 0 00-2-2h-1m-7 15v-4h4v4M5 21H3m2 0V8a2 2 0 012-2h1m0 0V5a2 2 0 012-2h2a2 2 0 012 2v1M9 7h.01M9 11h.01M13 7h.01M13 11h.01" />
          </svg>
        ),
      },
    ],
  },
];

const flatNavItems: NavItem[] = navGroups.flatMap((g) => g.items);

/** Shown while a lazy page chunk is still downloading. */
const PageContentLoader: React.FC = () => (
  <div className="flex min-h-[60vh] flex-1 flex-col items-center justify-center gap-3">
    <Spinner size="lg" color="navy" />
    <p className="text-[13px] text-textSubtle">Loading page…</p>
  </div>
);

interface GlobalLayoutProps {
  children: ReactNode;
}

const GlobalLayout: React.FC<GlobalLayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Pick the single most-specific nav item for the current URL. Without this,
  // a parent prefix like "/drivers" also lit up when we were on
  // "/drivers/on-shift" — dispatchers saw two highlighted rows and couldn't
  // tell which page they were actually on. Longest matching prefix wins; ties
  // resolve to the first item declared (nav ordering is intentional).
  const activeItemId = useMemo(() => {
    const matchesPrefix = (prefix: string) =>
      location.pathname === prefix || location.pathname.startsWith(prefix + '/');
    let bestId: string | null = null;
    let bestLen = -1;
    for (const item of flatNavItems) {
      const prefixes = [item.matchPrefix, item.altPrefix].filter(
        (p): p is string => !!p,
      );
      let candidate = -1;
      for (const p of prefixes) {
        if (matchesPrefix(p) && p.length > candidate) candidate = p.length;
      }
      if (candidate === -1 && !item.matchPrefix && !item.altPrefix) {
        // Items with no prefix rules match only by exact path equality.
        if (location.pathname === item.path) candidate = item.path.length;
      }
      if (candidate > bestLen) {
        bestLen = candidate;
        bestId = item.id;
      }
    }
    return bestId;
  }, [location.pathname]);

  const isActive = (item: NavItem) => item.id === activeItemId;

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : 'U';

  // Brand-name substitution: when the backend account's name is the literal brand,
  // display it in the stylised "U CARZ" form rather than "Ucarz".
  const brandify = (s: string) => s.replace(/\bUcarz\b/gi, 'U CARZ');
  const fullName = user ? brandify(`${user.firstName} ${user.lastName}`) : 'User';
  const role = user?.role ?? 'Operator';

  return (
    <div className="flex h-screen overflow-hidden bg-background text-textMain">

      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 w-[220px] flex flex-col gradient-brand text-sidebarFg',
          'shadow-sidebar transition-transform duration-300 ease-in-out',
          'lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Brand */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-sidebarBorder shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src={logoWhite} alt="U CARZ" className="h-6 w-auto shrink-0 object-contain" />
            <div className="h-5 w-px bg-white/20 shrink-0" aria-hidden />
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/60 font-semibold leading-[1.3] shrink-0">
              Operations
              <br />
              Suite
            </div>
          </div>
          {/* Close btn – mobile */}
          <button
            className="lg:hidden p-1 rounded text-sidebarMuted hover:text-sidebarFg"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2.5" aria-label="Main navigation">
          {navGroups.map((group, gi) => (
            <div key={group.id} className={cn(gi > 0 && 'mt-2')}>
              <p className="px-2.5 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebarMuted/70">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item);
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        'group relative flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium transition-all duration-150',
                        active
                          ? 'bg-white/10 text-white shadow-inner-light'
                          : 'text-sidebarFg/75 hover:bg-white/5 hover:text-white'
                      )}
                      aria-current={active ? 'page' : undefined}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'absolute left-0 top-1/2 -translate-y-1/2 w-0.5 rounded-r-full bg-white transition-all',
                          active ? 'h-5 opacity-100' : 'h-0 opacity-0'
                        )}
                      />
                      <span className={cn('shrink-0', active ? 'text-white' : 'text-sidebarMuted group-hover:text-white/90')}>
                        {item.icon}
                      </span>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && (
                        <span className="text-[9px] font-bold px-1.5 py-[1px] rounded uppercase tracking-wider bg-success/20 text-success/90 ring-1 ring-success/30">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="shrink-0 border-t border-sidebarBorder p-2.5">
          <Menu as="div" className="relative">
            <MenuButton
              className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white/5 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              aria-label="Account menu"
              title="Open account menu"
            >
              <div className="w-9 h-9 rounded-lg bg-white/10 ring-1 ring-white/15 flex items-center justify-center text-white text-[12px] font-bold shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0 leading-tight">
                <p className="text-[13px] font-semibold text-white truncate">{fullName}</p>
                <p className="text-[11px] text-sidebarMuted truncate">{user?.email}</p>
              </div>
              <span className="p-1 text-sidebarMuted shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 9l4-4 4 4M8 15l4 4 4-4" />
                </svg>
              </span>
            </MenuButton>
            <MenuItems
              anchor="top end"
              className="z-50 mt-[-8px] w-52 rounded-lg bg-surface border border-borderLight shadow-floating py-1 focus:outline-none"
            >
              <div className="px-3 py-2 border-b border-borderLight">
                <p className="text-[11px] uppercase tracking-wider text-textSubtle font-semibold">Signed in as</p>
                <p className="text-sm font-semibold text-textMain truncate">{user?.email}</p>
                <p className="text-[11px] text-textMuted mt-0.5 capitalize">{String(role).toLowerCase()}</p>
              </div>
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => navigate('/settings/security')}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm text-textMain',
                      focus && 'bg-surfaceMuted'
                    )}
                  >
                    <svg className="w-4 h-4 text-textMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6}
                        d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.764-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings & Security
                  </button>
                )}
              </MenuItem>
              <div className="my-1 border-t border-borderLight" />
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm text-error',
                      focus && 'bg-errorLight/40'
                    )}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </aside>

      {/* ── Right column: topbar + content ── */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="shrink-0 h-14 bg-surface/90 backdrop-blur-md border-b border-borderLight flex items-center px-3 sm:px-5 gap-2 z-10 shadow-nav">
          {/* Hamburger – mobile */}
          <button
            className="lg:hidden icon-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Breadcrumb / page title */}
          <div className="flex-1 min-w-0">
            <PageBreadcrumb location={location.pathname} />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-0.5">
            <ThemeToggle className="hidden md:inline-flex" />
            <ThemeToggle compact className="md:hidden" />

            <MessagesBell onOpen={() => navigate('/messages')} />

            <button
              className="icon-btn relative"
              aria-label="Notifications"
            >
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-error rounded-full ring-2 ring-surface" aria-hidden="true" />
            </button>

            <span className="divider-y hidden sm:block" />

            <div className="hidden sm:flex items-center gap-2 pl-1 pr-1">
              <div className="w-8 h-8 rounded-md gradient-brand flex items-center justify-center text-white text-[11px] font-bold ring-1 ring-borderMedium">
                {initials}
              </div>
              <div className="leading-tight max-w-[140px]">
                <p className="text-[12px] font-semibold text-textMain truncate">{fullName}</p>
                <p className="text-[10px] text-textMuted uppercase tracking-wider truncate">{String(role).toLowerCase()}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content. */}
        <main ref={mainRef} className="flex-1 overflow-y-auto overscroll-none bg-background">
          {/* The boundary sits inside <main> on purpose: a page that fails to load
              leaves the sidebar and header mounted, so the user can navigate away. */}
          <RouteErrorBoundary label="page content">
            {/* Nearest Suspense boundary for the lazy page chunks. Having it here
                (rather than only app-wide in AppRoutes) means a slow chunk shows a
                spinner in the content area instead of blanking the whole shell. */}
            <Suspense fallback={<PageContentLoader />}>{children}</Suspense>
          </RouteErrorBoundary>
        </main>

        {/* Keyboard shortcuts hint strip — dispatch dashboard only */}
        {(location.pathname === '/dispatch' || location.pathname.startsWith('/dispatch/')) && (
          <div
            className="shrink-0 h-9 flex items-center justify-center gap-2.5 px-4 border-t border-borderLight bg-surface text-[12px] font-medium text-textMain select-none shadow-[0_-1px_0_rgba(0,0,0,0.02)]"
            aria-label="Keyboard shortcuts"
          >
            <span className="text-textMuted">Press</span>
            <kbd className="inline-flex items-center justify-center min-w-[32px] h-[22px] px-2 rounded-md border border-borderMedium bg-surfaceMuted text-[11px] font-bold text-primary font-mono leading-none shadow-sm ring-1 ring-inset ring-white/40">
              Esc
            </kbd>
            <span className="text-textMain">to close the open modal</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Breadcrumb helper ──────────────────────────────────────────────────────────
const routeLabels: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/onboarding/welcome': 'Driver Onboarding › Welcome',
  '/onboarding/personal': 'Driver Onboarding › Personal Profile',
  '/onboarding/licensing': 'Driver Onboarding › Licensing',
  '/onboarding/insurance': 'Driver Onboarding › Insurance & DBS',
  '/onboarding/mandatory': 'Driver Onboarding › Mandatory Documents',
  '/onboarding/bank': 'Driver Onboarding › Bank Account',
  '/onboarding/additional': 'Driver Onboarding › Additional Info',
  '/onboarding/summary': 'Driver Onboarding › Summary',
  '/onboarding/success': 'Driver Onboarding › Complete',
  '/training': 'Training & Certifications',
  '/bookings': 'Booking Management',
  '/accounts': 'Accounts',
  '/accounts/new': 'Accounts › Create Account',
  '/vehicles': 'Vehicles',
  '/vehicles/new': 'Vehicles › Add New Vehicle',
  '/places': 'Places',
  '/places/new': 'Places › Add Place',
  '/drivers': 'Drivers',
  '/drivers/new': 'Drivers › Add New Driver',
  '/pas': 'Passenger Assistants',
  '/pas/new': 'Passenger Assistants › Add New Assistant',
  '/passengers': 'Passengers',
  '/passengers/new': 'Passengers › Add Passenger',
  '/messages': 'Messages',
  '/admin/option-sets': 'Admin › Option Sets',
  '/admin/working-hours-preferences': 'Admin › Working Hours',
  '/admin/bulk-import': 'Admin › Bulk Import',
  '/holidays': 'Public Holidays',
  '/licensing-councils': 'Councils',
  '/licensing-councils/new': 'Councils › Add Council',
  '/subsidiaries': 'Subsidiaries',
  '/subsidiaries/new': 'Subsidiaries › Add Subsidiary',
  '/reports/drivers/monthly-payment': 'Reports › Driver Payments',
};

const PageBreadcrumb: React.FC<{ location: string }> = ({ location }) => {
  const label = routeLabels[location] ?? 'U CARZ';
  const parts = label.split(' › ');
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex items-center gap-1.5 text-[13px]">
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <li className="text-textSubtle" aria-hidden="true">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </li>
            )}
            <li className={i === parts.length - 1 ? 'font-semibold text-textMain truncate' : 'text-textMuted'}>
              {part}
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  );
};

// ── Messages bell ──────────────────────────────────────────────────────────────
// Unread-count badge in the header. Backed by useQuery on the messaging inbox count,
// invalidated on every SignalR MessageReceived event (see messageHub.ts), and polled
// every 60s as a fallback for missed events. Click navigates to /messages.
const MessagesBell: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { data: unread } = useQuery(
    MESSAGING_QUERY_KEY.unreadCount,
    () => messagingService.unreadCount(),
    { refetchInterval: 60_000, staleTime: 30_000 },
  );
  const count = unread?.count ?? 0;
  const hasUnread = count > 0;
  return (
    <button
      onClick={onOpen}
      className="icon-btn relative"
      aria-label={hasUnread ? `Messages, ${count} unread` : 'Messages'}
      title={hasUnread ? `${count} unread message${count === 1 ? '' : 's'}` : 'Messages'}
    >
      <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
      {hasUnread && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-error text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-surface">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

export { flatNavItems };
export default GlobalLayout;
